import { useState, useEffect, useCallback, useMemo } from "react";
import { AttendanceService } from "@/services/Attendance/AttendanceService";
import type { IPageInfo } from "@/interface/common.interface";
import type {
  ISalaryPreviewData,
  ISalaryPreviewError,
  ISalaryPreviewUser,
  IPreviewMonthlySalaryPayload,
} from "@/interface/attendance.interface";
import type { ISalaryHead } from "@/interface/salaryHead.interface";
import { ThrowError } from "@/api/callApi";
import { showToastNotification } from "@/utility/helper";
import { createInitialPageInfo } from "./createSalary.constants";
import {
  buildCreateMonthlySalaryPayload,
  filterSalaryPreviewList,
  getSelectedUserIds,
  paginateList,
  resetPage,
  type SalarySubTab,
} from "./createSalary.utils";
import type { UseMasterDataReturn } from "./useMasterData";

export const useSalaryPreviewTab = (
  masterData: UseMasterDataReturn,
  selectedEmployeeIdsRef: React.RefObject<Set<string | number>>,
  onSalaryCreatedSuccess?: () => void,
) => {
  const [salaryPreviewData, setSalaryPreviewData] =
    useState<ISalaryPreviewData | null>(null);
  const [isLoadingSalaryPreview, setIsLoadingSalaryPreview] =
    useState<boolean>(false);
  const [isCreatingSalary, setIsCreatingSalary] = useState<boolean>(false);
  const [showCreateSalaryConfirmModal, setShowCreateSalaryConfirmModal] =
    useState<boolean>(false);
  const [salaryPreviewPaginationData, setSalaryPreviewPaginationData] =
    useState<IPageInfo>(createInitialPageInfo);
  const [salarySubTab, setSalarySubTab] = useState<SalarySubTab>("users");

  // Custom salary preview users state (allows dynamic addition/modification of user salary heads)
  const [customSalaryPreviewUsers, setCustomSalaryPreviewUsers] = useState<
    ISalaryPreviewUser[]
  >([]);
  const [selectedUserForAddHead, setSelectedUserForAddHead] =
    useState<ISalaryPreviewUser | null>(null);
  const [showAddSalaryHeadModal, setShowAddSalaryHeadModal] =
    useState<boolean>(false);

  // Sync customSalaryPreviewUsers when API data changes
  useEffect(() => {
    if (salaryPreviewData?.users) {
      setCustomSalaryPreviewUsers(salaryPreviewData.users);
    } else {
      setCustomSalaryPreviewUsers([]);
    }
  }, [salaryPreviewData]);

  const salaryPreviewUsers = customSalaryPreviewUsers;

  const salaryPreviewErrors = useMemo(
    () => salaryPreviewData?.errors || [],
    [salaryPreviewData],
  );

  // Fetch salary preview
  const fetchSalaryPreview = useCallback(async () => {
    if (!masterData.selectedBranchId && !masterData.selectedSiteId) return;

    setIsLoadingSalaryPreview(true);
    try {
      const currentIds = selectedEmployeeIdsRef.current ?? new Set();
      const payload: IPreviewMonthlySalaryPayload = {
        userIds: getSelectedUserIds(currentIds),
        startDate: masterData.attendanceStartDate,
        endDate: masterData.attendanceEndDate,
      };

      const response = await AttendanceService.previewMonthlySalary(payload);
      const rawData: ISalaryPreviewData | null = response?.data || null;
      if (rawData) {
        setSalaryPreviewData(rawData);
        const validUsers = rawData.users?.length || 0;
        const errorUsers = rawData.errors?.length || 0;
        const totalUsers = validUsers + errorUsers;
        setSalaryPreviewPaginationData((prev) => ({
          ...prev,
          total: totalUsers,
          totalPages: Math.ceil(totalUsers / prev.pageSize) || 1,
        }));
      } else {
        setSalaryPreviewData(null);
        setSalaryPreviewPaginationData((prev) => ({
          ...prev,
          total: 0,
          totalPages: 0,
        }));
      }
    } catch (error) {
      ThrowError(error);
      setSalaryPreviewData(null);
    } finally {
      setIsLoadingSalaryPreview(false);
    }
  }, [
    masterData.selectedBranchId,
    masterData.selectedSiteId,
    selectedEmployeeIdsRef,
    masterData.attendanceStartDate,
    masterData.attendanceEndDate,
  ]);

  // Fetch when tab becomes active
  useEffect(() => {
    if (masterData.activeTab === "salary") fetchSalaryPreview();
  }, [masterData.activeTab, fetchSalaryPreview]);

  // Auto-switch sub-tab if active sub-tab is empty
  useEffect(() => {
    if (salaryPreviewUsers.length === 0 && salaryPreviewErrors.length > 0) {
      setSalarySubTab("errors");
    } else if (
      salaryPreviewUsers.length > 0 &&
      salaryPreviewErrors.length === 0
    ) {
      setSalarySubTab("users");
    }
  }, [salaryPreviewUsers.length, salaryPreviewErrors.length]);

  // Reset pagination page when switching sub-tab or searching
  useEffect(() => {
    setSalaryPreviewPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  }, [salarySubTab, masterData.searchValue]);

  // Single active salary list (filtered by search)
  const filteredSalaryList = useMemo<
    Array<ISalaryPreviewUser | ISalaryPreviewError>
  >(() => {
    if (salarySubTab === "errors") {
      return filterSalaryPreviewList(
        salaryPreviewErrors,
        masterData.searchValue,
      );
    }
    return filterSalaryPreviewList(salaryPreviewUsers, masterData.searchValue);
  }, [
    salarySubTab,
    salaryPreviewUsers,
    salaryPreviewErrors,
    masterData.searchValue,
  ]);

  // Paginated data slice
  const paginatedSalaryData = useMemo(() => {
    return paginateList(filteredSalaryList, salaryPreviewPaginationData);
  }, [filteredSalaryList, salaryPreviewPaginationData]);

  // Sync total and totalPages with active list length
  useEffect(() => {
    const total = filteredSalaryList.length;
    const limit = salaryPreviewPaginationData.pageSize || 10;
    const totalPages = Math.ceil(total / limit) || 1;
    setSalaryPreviewPaginationData((prev) => ({ ...prev, total, totalPages }));
  }, [filteredSalaryList.length, salaryPreviewPaginationData.pageSize]);

  // --- Add/Remove salary head handlers ---

  const handleOpenAddSalaryHeadModal = useCallback(
    (user: ISalaryPreviewUser) => {
      setSelectedUserForAddHead(user);
      setShowAddSalaryHeadModal(true);
    },
    [],
  );

  const handleCloseAddSalaryHeadModal = useCallback(() => {
    setShowAddSalaryHeadModal(false);
    setSelectedUserForAddHead(null);
  }, []);

  const handleAddSalaryHead = useCallback(
    (
      userId: string,
      salaryHeadId: string,
      amount: number,
      headType: "earning" | "deduction",
      headName?: string,
    ) => {
      setCustomSalaryPreviewUsers((prevUsers) => {
        return prevUsers.map((item) => {
          if (String(item.user?.id) !== String(userId)) return item;

          const currentSalaryList: ISalaryHead[] = [...(item.userSalary || [])];

          const existingIndex = currentSalaryList.findIndex(
            (h) =>
              String(h.id) === String(salaryHeadId) ||
              String(h.branchSalaryHeadId) === String(salaryHeadId),
          );

          if (existingIndex > -1) {
            const targetHead = currentSalaryList[existingIndex];
            const origAmt =
              targetHead.originalAmount !== undefined
                ? targetHead.originalAmount
                : Number(targetHead.amount) || 0;

            currentSalaryList[existingIndex] = {
              ...targetHead,
              amount: amount,
              type: headType,
              isCustomAdded: true,
              originalAmount: origAmt,
            };
          } else {
            const newHead: ISalaryHead = {
              id: salaryHeadId,
              branchSalaryHeadId: salaryHeadId,
              name:
                headName ||
                (headType === "earning" ? "Other Earning" : "Other Deduction"),
              salaryName: headName,
              type: headType,
              amountType: "value",
              taxExempted: false,
              frequency: null,
              payableIn: null,
              fixVariable: null,
              isActive: true,
              partOfForm16: false,
              wantToShowInOfferLetter: false,
              isBase: false,
              takeInput: true,
              canAppearInBasedOn: false,
              fromEmployeer: false,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              deletedAt: null,
              amount: amount,
              isCustomAdded: true,
              originalAmount: 0,
            };
            currentSalaryList.push(newHead);
          }

          // Recalculate totals
          let totalEarnings = 0;
          let totalDeductions = 0;

          const isGrossHead = (name: string) => {
            const n = name.toLowerCase().trim();
            return (
              n.includes("gross salary") ||
              n.includes("gross-salary") ||
              n.includes("gross_salary") ||
              n === "gross" ||
              n === "gross pay"
            );
          };

          const isNetHead = (name: string) => {
            const n = name.toLowerCase().trim();
            return (
              n.includes("net salary") ||
              n.includes("net-salary") ||
              n.includes("net_salary") ||
              n === "net pay" ||
              n === "net"
            );
          };

          const isAttendanceDeductionHead = (name: string) => {
            const n = name.toLowerCase().trim();
            return (
              n.includes("absent") ||
              n.includes("leave deduction") ||
              n.includes("leave-deduction") ||
              n.includes("leave_deduction") ||
              n.includes("attendance deduction") ||
              n.includes("attendance-deduction") ||
              n.includes("attendance_deduction")
            );
          };

          currentSalaryList.forEach((h) => {
            const val = Number(h.amount) || 0;
            const name = (h.name || h.salaryName || "").trim();
            const isNet = isNetHead(name);
            const isGross = isGrossHead(name);
            const isAttDeduct = isAttendanceDeductionHead(name);

            // Skip summary heads and attendance deduction heads (handled via attDeduction)
            if (isNet || isGross || isAttDeduct) return;

            const rawType = (h.type || "").toLowerCase().trim();
            if (rawType === "earning") {
              totalEarnings += val;
            } else if (rawType === "deduction") {
              totalDeductions += val;
            }
          });

          const attDeduction =
            item.attendanceSummary?.totalAttendanceDeductionAmount ??
            (item.attendanceSummary?.absentDeductionAmount || 0) +
              (item.attendanceSummary?.leaveDeductionAmount || 0);

          const finalTotalDeductions = Number(
            (totalDeductions + attDeduction).toFixed(2),
          );
          const finalTotalEarnings = Number(totalEarnings.toFixed(2));
          const finalNetSalary = Number(
            Math.max(0, finalTotalEarnings - finalTotalDeductions).toFixed(2),
          );

          // Update any Gross & Net summary rows in the salary list to stay in sync
          const finalSalaryList = currentSalaryList.map((h) => {
            const name = (h.name || h.salaryName || "").trim();
            if (isGrossHead(name)) {
              return { ...h, amount: finalTotalEarnings };
            }
            if (isNetHead(name)) {
              return { ...h, amount: finalNetSalary };
            }
            return h;
          });

          return {
            ...item,
            userSalary: finalSalaryList,
            totals: {
              earning: finalTotalEarnings,
              deduction: finalTotalDeductions,
              netSalary: finalNetSalary,
            },
          };
        });
      });

      showToastNotification("success", "Salary head updated successfully!");
    },
    [],
  );

  const handleRemoveSalaryHead = useCallback(
    (userId: string, salaryHeadId: string) => {
      setCustomSalaryPreviewUsers((prevUsers) => {
        return prevUsers.map((item) => {
          if (String(item.user?.id) !== String(userId)) return item;

          const currentSalaryList: ISalaryHead[] = (item.userSalary || []).map(
            (h) => {
              const hId = String(h.branchSalaryHeadId || h.id);
              if (hId === String(salaryHeadId)) {
                const restoredAmount = h.originalAmount ?? 0;
                return {
                  ...h,
                  amount: restoredAmount,
                  isCustomAdded: false,
                };
              }
              return h;
            },
          );

          // Recalculate totals
          let totalEarnings = 0;
          let totalDeductions = 0;

          const isGrossHead = (name: string) => {
            const n = name.toLowerCase().trim();
            return (
              n.includes("gross salary") ||
              n.includes("gross-salary") ||
              n.includes("gross_salary") ||
              n === "gross" ||
              n === "gross pay"
            );
          };

          const isNetHead = (name: string) => {
            const n = name.toLowerCase().trim();
            return (
              n.includes("net salary") ||
              n.includes("net-salary") ||
              n.includes("net_salary") ||
              n === "net pay" ||
              n === "net"
            );
          };

          const isAttendanceDeductionHead = (name: string) => {
            const n = name.toLowerCase().trim();
            return (
              n.includes("absent") ||
              n.includes("leave deduction") ||
              n.includes("leave-deduction") ||
              n.includes("leave_deduction") ||
              n.includes("attendance deduction") ||
              n.includes("attendance-deduction") ||
              n.includes("attendance_deduction")
            );
          };

          currentSalaryList.forEach((h) => {
            const val = Number(h.amount) || 0;
            const name = (h.name || h.salaryName || "").trim();
            const isNet = isNetHead(name);
            const isGross = isGrossHead(name);
            const isAttDeduct = isAttendanceDeductionHead(name);

            // Skip summary heads and attendance deduction heads (handled via attDeduction)
            if (isNet || isGross || isAttDeduct) return;

            const rawType = (h.type || "").toLowerCase().trim();
            if (rawType === "earning") {
              totalEarnings += val;
            } else if (rawType === "deduction") {
              totalDeductions += val;
            }
          });

          const attDeduction =
            item.attendanceSummary?.totalAttendanceDeductionAmount ??
            (item.attendanceSummary?.absentDeductionAmount || 0) +
              (item.attendanceSummary?.leaveDeductionAmount || 0);

          const finalTotalDeductions = Number(
            (totalDeductions + attDeduction).toFixed(2),
          );
          const finalTotalEarnings = Number(totalEarnings.toFixed(2));
          const finalNetSalary = Number(
            Math.max(0, finalTotalEarnings - finalTotalDeductions).toFixed(2),
          );

          // Update any Gross & Net summary rows in the salary list to stay in sync
          const finalSalaryList = currentSalaryList.map((h) => {
            const name = (h.name || h.salaryName || "").trim();
            if (isGrossHead(name)) {
              return { ...h, amount: finalTotalEarnings };
            }
            if (isNetHead(name)) {
              return { ...h, amount: finalNetSalary };
            }
            return h;
          });

          return {
            ...item,
            userSalary: finalSalaryList,
            totals: {
              earning: finalTotalEarnings,
              deduction: finalTotalDeductions,
              netSalary: finalNetSalary,
            },
          };
        });
      });

      showToastNotification("success", "Salary head removed successfully!");
    },
    [],
  );

  // --- Create monthly salary ---

  const handleCreateMonthlySalary = useCallback(async () => {
    if (!salaryPreviewUsers || salaryPreviewUsers.length === 0) {
      showToastNotification(
        "danger",
        "No ready users available to create salary.",
      );
      return;
    }

    if (!masterData.selectedSiteId) {
      showToastNotification(
        "danger",
        "Please select a site to create monthly salary.",
      );
      return;
    }

    const payload = buildCreateMonthlySalaryPayload(
      salaryPreviewUsers,
      masterData.selectedSiteId,
      masterData.attendanceStartDate,
      masterData.attendanceEndDate,
    );

    setIsCreatingSalary(true);
    try {
      const response = await AttendanceService.createUserMonthlySalary(payload);
      if (
        response?.status ||
        response?.statuscode === 200 ||
        response?.statuscode === 201
      ) {
        showToastNotification(
          "success",
          response?.message || "Monthly salary created successfully!",
        );
        selectedEmployeeIdsRef.current = new Set();
        onSalaryCreatedSuccess?.();
        masterData.handleTabChange("employee");
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to create monthly salary.",
        );
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to create monthly salary.";
      showToastNotification("danger", message);
    } finally {
      setIsCreatingSalary(false);
    }
  }, [
    salaryPreviewUsers,
    masterData.selectedSiteId,
    masterData.attendanceStartDate,
    masterData.attendanceEndDate,
    masterData.handleTabChange,
    selectedEmployeeIdsRef,
    onSalaryCreatedSuccess,
  ]);

  const openCreateSalaryConfirmModal = useCallback(() => {
    if (!salaryPreviewUsers || salaryPreviewUsers.length === 0) {
      showToastNotification(
        "danger",
        "No ready users available to create salary.",
      );
      return;
    }

    if (!masterData.selectedSiteId) {
      showToastNotification(
        "danger",
        "Please select a site to create monthly salary.",
      );
      return;
    }

    setShowCreateSalaryConfirmModal(true);
  }, [salaryPreviewUsers, masterData.selectedSiteId]);

  const closeCreateSalaryConfirmModal = useCallback(() => {
    if (!isCreatingSalary) {
      setShowCreateSalaryConfirmModal(false);
    }
  }, [isCreatingSalary]);

  const handleConfirmCreateSalary = useCallback(async () => {
    await handleCreateMonthlySalary();
    setShowCreateSalaryConfirmModal(false);
  }, [handleCreateMonthlySalary]);

  const handleSalaryPreviewChangePageSize = useCallback((newSize: number) => {
    setSalaryPreviewPaginationData((prev) => ({
      ...prev,
      pageSize: newSize,
      currentPage: 1,
    }));
  }, []);

  const resetPagination = useCallback(() => {
    setSalaryPreviewPaginationData(resetPage);
  }, []);

  return {
    salaryPreviewData,
    salaryPreviewUsers,
    salaryPreviewErrors,
    paginatedSalaryData,
    isLoadingSalaryPreview,
    isCreatingSalary,
    showCreateSalaryConfirmModal,
    salaryPreviewPaginationData,
    setSalaryPreviewPaginationData,
    salarySubTab,
    setSalarySubTab,
    selectedUserForAddHead,
    showAddSalaryHeadModal,
    handleOpenAddSalaryHeadModal,
    handleCloseAddSalaryHeadModal,
    handleAddSalaryHead,
    handleRemoveSalaryHead,
    handleCreateMonthlySalary,
    openCreateSalaryConfirmModal,
    closeCreateSalaryConfirmModal,
    handleConfirmCreateSalary,
    handleSalaryPreviewChangePageSize,
    resetPagination,
    refetchSalaryPreview: fetchSalaryPreview,
  };
};

export type UseSalaryPreviewTabReturn = ReturnType<typeof useSalaryPreviewTab>;
